import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Trash2,
  Plus,
  Clock,
  FileText,
  Eye,
  CheckCircle,
  Power,
  X,
  Store,
  User,
  CreditCard,
  XCircle,
  RefreshCw,
  AlertCircle,
  Bell,
  Loader2,
  Edit,
} from "lucide-react";

export const AdminMerchantsTab = ({
  merchants,
  merchantSearch,
  setMerchantSearch,
  merchantFilter,
  setMerchantFilter,
  selectedMerchantIds,
  setSelectedMerchantIds,
  bulkTogglingStores,
  sendingReminder,
  pendingMerchants,
  pendingPayment,
  pendingDocuments,
  getFilteredMerchants,
  handleBulkRemoveTestMerchants,
  handleSeedDemoAccounts,
  viewMerchantDetail,
  viewMerchantDocuments,
  openMerchantProfileModal,
  openSyncConnectModal,
  openViewAgreementModal,
  openSignAgreementModal,
  openAgreementModal,
  openFixSubscriptionModal,
  handleSendReminder,
  handleResetAgreement,
  handleDeleteMerchant,
  handlePermanentDeleteMerchant,
  handleCreateConnect,
  handleToggleFeature,
  handleToggleStoreStatus,
  handleBulkToggleStoreStatus,
  toggleSelectAllMerchants,
  toggleMerchantSelection,
  setSelectedMerchant,
  setShowMerchantDetail,
}) => {
  const approvedMerchants = merchants.filter((m) => m.is_approved);
  const rejectedMerchants = merchants.filter((m) => m.merchant_status === "rejected");

  return (
    <div className="space-y-6" data-testid="admin-merchants-tab">
      {/* Merchant Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3">
          <span className="text-slate-500">
            {getFilteredMerchants().length} of {merchants.length} merchants
          </span>
          {pendingMerchants.length > 0 && (
            <span className="text-amber-600 font-medium px-2 py-1 bg-amber-50 rounded">
              {pendingMerchants.length} pending
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Input
            placeholder="Search merchants..."
            value={merchantSearch}
            onChange={(e) => setMerchantSearch(e.target.value)}
            className="w-48"
          />
          <Select value={merchantFilter} onValueChange={setMerchantFilter}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="trial">Trial</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="test">Test</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkRemoveTestMerchants}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Remove Test
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSeedDemoAccounts}
            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
          >
            <Plus className="h-4 w-4 mr-1" />
            Seed Demo
          </Button>
        </div>
      </div>

      {/* Pending Approval - Documents Submitted */}
      {pendingMerchants.length > 0 && merchantFilter === "all" && (
        <div className="space-y-4">
          <h2 className="font-heading font-semibold text-slate-900">
            <Clock className="h-4 w-4 inline mr-2 text-amber-500" />
            Pending Approval ({pendingMerchants.length})
          </h2>
          <div className="grid gap-4">
            {pendingMerchants.map((merchant, idx) => (
              <div
                key={merchant.user_id}
                className="bg-white rounded-xl border border-amber-200 p-6"
                data-testid={`pending-merchant-${idx}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-slate-900">{merchant.name}</h3>
                    <p className="text-sm text-slate-500">{merchant.email}</p>
                    {merchant.business_name && (
                      <p className="text-sm text-indigo-600 mt-1">{merchant.business_name}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        {merchant.documents_count || 0} documents submitted
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => viewMerchantDetail(merchant)}
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Details
                    </Button>
                    <Button
                      onClick={() => viewMerchantDocuments(merchant)}
                      size="sm"
                      className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                    >
                      Review Documents
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approved Merchants */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-slate-900">
            <CheckCircle className="h-4 w-4 inline mr-2 text-emerald-500" />
            Approved Merchants ({approvedMerchants.length})
          </h2>

          {/* Bulk Actions */}
          {selectedMerchantIds.length > 0 && (
            <div className="flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2">
              <span className="text-sm text-slate-600">{selectedMerchantIds.length} selected</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleBulkToggleStoreStatus(true)}
                disabled={bulkTogglingStores}
                className="text-xs bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                data-testid="bulk-set-online-btn"
              >
                <Power className="h-3 w-3 mr-1" />
                Set Online
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleBulkToggleStoreStatus(false)}
                disabled={bulkTogglingStores}
                className="text-xs bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                data-testid="bulk-set-offline-btn"
              >
                <Power className="h-3 w-3 mr-1" />
                Set Offline
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedMerchantIds([])}
                className="text-xs"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        {approvedMerchants.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Store className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No approved merchants yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full min-w-[1400px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500">
                    <input
                      type="checkbox"
                      checked={
                        selectedMerchantIds.length === approvedMerchants.length &&
                        approvedMerchants.length > 0
                      }
                      onChange={toggleSelectAllMerchants}
                      className="w-4 h-4 rounded border-slate-300"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Merchant</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Business</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500">Store Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Plan</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Stripe ID</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Documents</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Payment Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Revenue</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Commission</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500">CRM</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500">POS</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {approvedMerchants.map((merchant) => (
                  <tr
                    key={merchant.user_id}
                    className={`border-t border-slate-100 hover:bg-slate-50 ${
                      selectedMerchantIds.includes(merchant.user_id) ? "bg-indigo-50" : ""
                    }`}
                  >
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedMerchantIds.includes(merchant.user_id)}
                        onChange={() => toggleMerchantSelection(merchant.user_id)}
                        className="w-4 h-4 rounded border-slate-300"
                        data-testid={`select-merchant-${merchant.user_id}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{merchant.name}</div>
                      <div className="text-xs text-slate-500">{merchant.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{merchant.business_name || "-"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <div
                          className={`w-2.5 h-2.5 rounded-full ${
                            merchant.is_store_active !== false
                              ? "bg-emerald-500 animate-pulse"
                              : "bg-red-400"
                          }`}
                        />
                        <Switch
                          checked={merchant.is_store_active !== false}
                          onCheckedChange={(checked) => handleToggleStoreStatus(merchant, checked)}
                          data-testid={`store-toggle-${merchant.user_id}`}
                        />
                        <span
                          className={`text-xs font-medium ${
                            merchant.is_store_active !== false ? "text-emerald-600" : "text-red-500"
                          }`}
                        >
                          {merchant.is_store_active !== false ? "Online" : "Offline"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                        5% Commission
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-500 font-mono">
                        {merchant.stripe_customer_id
                          ? merchant.stripe_customer_id.slice(0, 14) + "..."
                          : "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-600">
                        {merchant.documents_count || 0} uploaded
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {merchant.is_approved ? (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                          Active
                        </span>
                      ) : merchant.merchant_status === "under_review" ? (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
                          Under Review
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      £{((merchant.total_revenue || 0) / 100).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-emerald-600 font-medium">
                        £{((merchant.commission_earned || 0) / 100).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Switch
                        checked={merchant.feature_access?.crm === true}
                        onCheckedChange={() =>
                          handleToggleFeature(merchant.user_id, "crm", merchant.feature_access?.crm)
                        }
                        data-testid={`crm-toggle-${merchant.user_id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Switch
                        checked={merchant.feature_access?.pos === true}
                        onCheckedChange={() =>
                          handleToggleFeature(merchant.user_id, "pos", merchant.feature_access?.pos)
                        }
                        data-testid={`pos-toggle-${merchant.user_id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openMerchantProfileModal(merchant)}
                          className="text-xs"
                          data-testid={`merchant-details-btn-${merchant.user_id}`}
                        >
                          <User className="h-3 w-3 mr-1" />
                          Details
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewMerchantDocuments(merchant)}
                          className="text-xs text-indigo-600 hover:bg-indigo-50"
                          title="Review Documents"
                          data-testid={`view-documents-btn-${merchant.user_id}`}
                        >
                          <FileText className="h-3 w-3 mr-1" />
                          Docs
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openViewAgreementModal(merchant)}
                          className="text-xs"
                          title="View Agreement"
                          data-testid={`view-agreement-btn-${merchant.user_id}`}
                        >
                          <FileText className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openSyncConnectModal(merchant)}
                          className="text-xs text-blue-600 hover:bg-blue-50"
                          title="Link Stripe Account"
                          data-testid={`link-stripe-btn-${merchant.user_id}`}
                        >
                          <CreditCard className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleCreateConnect(merchant.email, merchant.name || merchant.business_name)
                          }
                          className="text-xs text-green-600 hover:bg-green-50"
                          title="Create Stripe Account"
                          data-testid={`create-stripe-btn-${merchant.user_id}`}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedMerchant(merchant);
                            setShowMerchantDetail(true);
                          }}
                          className="text-xs"
                          title="View Details"
                          data-testid={`merchant-view-btn-${merchant.user_id}`}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteMerchant(merchant.user_id)}
                          className="text-xs text-orange-600 hover:bg-orange-50"
                          title="Disqualify"
                          data-testid={`disqualify-btn-${merchant.user_id}`}
                        >
                          <XCircle className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePermanentDeleteMerchant(merchant.user_id, merchant.email)}
                          className="text-xs text-red-600 hover:bg-red-50"
                          title="Delete Permanently"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pending Payment / Agreement */}
      {pendingPayment.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading font-semibold text-slate-900">
            <CreditCard className="h-4 w-4 inline mr-2 text-purple-500" />
            Pending Payment / Agreement ({pendingPayment.length})
          </h2>
          <div className="grid gap-4">
            {pendingPayment.map((merchant, idx) => (
              <div
                key={merchant.user_id}
                className="bg-white rounded-xl border border-purple-200 p-6"
                data-testid={`pending-payment-merchant-${idx}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-slate-900">{merchant.name}</h3>
                    <p className="text-sm text-slate-500">{merchant.email}</p>
                    {merchant.business_name && (
                      <p className="text-sm text-indigo-600 mt-1">{merchant.business_name}</p>
                    )}
                    <p className="text-xs text-purple-600 mt-2">
                      <CheckCircle className="h-3 w-3 inline mr-1" />
                      Documents approved - awaiting agreement & payment
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => openFixSubscriptionModal(merchant)}
                      size="sm"
                      className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid={`fix-subscription-btn-${merchant.user_id}`}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Fix/Approve Subscription
                    </Button>
                    <Button
                      onClick={() => handleSendReminder(merchant.user_id)}
                      size="sm"
                      variant="outline"
                      disabled={sendingReminder === merchant.user_id}
                      className="rounded-full border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                      data-testid={`reminder-btn-${merchant.user_id}`}
                    >
                      {sendingReminder === merchant.user_id ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Bell className="h-4 w-4 mr-1" />
                      )}
                      Send Reminder
                    </Button>
                    <Button
                      onClick={() => openMerchantProfileModal(merchant)}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-slate-200"
                    >
                      <User className="h-4 w-4 mr-1" />
                      View Details
                    </Button>
                    <Button
                      onClick={() => openAgreementModal(merchant)}
                      size="sm"
                      className="rounded-full bg-purple-600 hover:bg-purple-700"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Initiate Agreement
                    </Button>
                    <Button
                      onClick={() => openViewAgreementModal(merchant)}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-indigo-200 text-indigo-600"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Agreement
                    </Button>
                    <Button
                      onClick={() => openSignAgreementModal(merchant)}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-purple-200 text-purple-600"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Sign Agreement
                    </Button>
                    <Button
                      onClick={() => handleResetAgreement(merchant.user_id)}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-amber-200 text-amber-600 hover:bg-amber-50"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Reset Agreement
                    </Button>
                    <Button
                      onClick={() => handlePermanentDeleteMerchant(merchant.user_id, merchant.email)}
                      size="sm"
                      variant="outline"
                      className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete Permanently
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Awaiting Documents */}
      {pendingDocuments.length > 0 && merchantFilter === "all" && (
        <div className="space-y-4">
          <h2 className="font-heading font-semibold text-slate-900">
            <AlertCircle className="h-4 w-4 inline mr-2 text-blue-500" />
            Awaiting Documents ({pendingDocuments.length})
          </h2>
          <div className="grid gap-4">
            {pendingDocuments.map((merchant) => (
              <div key={merchant.user_id} className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">{merchant.name}</h3>
                    <p className="text-sm text-slate-500">{merchant.email}</p>
                    {merchant.business_name && (
                      <p className="text-sm text-indigo-600">{merchant.business_name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                      Waiting for documents
                    </span>
                    <Button
                      onClick={() => handleSendReminder(merchant.user_id)}
                      size="sm"
                      variant="outline"
                      disabled={sendingReminder === merchant.user_id}
                      className="border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                      data-testid={`reminder-doc-btn-${merchant.user_id}`}
                    >
                      {sendingReminder === merchant.user_id ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Bell className="h-4 w-4 mr-1" />
                      )}
                      Remind
                    </Button>
                    <Button
                      onClick={() => handleResetAgreement(merchant.user_id)}
                      size="sm"
                      variant="outline"
                      className="text-amber-600 hover:bg-amber-50 border-amber-200"
                      title="Reset agreement if stuck"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Reset Agreement
                    </Button>
                    <Button
                      onClick={() => handleDeleteMerchant(merchant.user_id)}
                      size="sm"
                      variant="outline"
                      className="text-orange-600 hover:bg-orange-50 border-orange-200"
                      title="Disqualify merchant (soft delete)"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Disqualify
                    </Button>
                    <Button
                      onClick={() => handlePermanentDeleteMerchant(merchant.user_id, merchant.email)}
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50 border-red-200"
                      title="Permanently delete merchant and all data"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete Permanently
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rejected Merchants */}
      {rejectedMerchants.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading font-semibold text-slate-900">
            <XCircle className="h-4 w-4 inline mr-2 text-red-500" />
            Rejected ({rejectedMerchants.length})
          </h2>
          <div className="grid gap-4">
            {rejectedMerchants.map((merchant) => (
              <div key={merchant.user_id} className="bg-white rounded-xl border border-red-100 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">{merchant.name}</h3>
                    <p className="text-sm text-slate-500">{merchant.email}</p>
                    {merchant.rejection_reason && (
                      <p className="text-sm text-red-600 mt-1">Reason: {merchant.rejection_reason}</p>
                    )}
                  </div>
                  <span className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded-full">Rejected</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
